import { MessageSquare, UserCheck, CreditCard, CheckCircle2, ShieldCheck, Gauge, Lock } from 'lucide-react';

export function SectionHeading({ kicker, title, children, id }) {
  return (
    <div className="mb-12 max-w-3xl" id={id}>
      <p className="font-tech text-xs sm:text-sm uppercase tracking-[0.3em] text-[#00f0ff] mb-3">{kicker}</p>
      <h2 className="font-heading text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-white nw-glitch">{title}</h2>
      {children && <p className="mt-4 text-slate-300 leading-relaxed">{children}</p>}
    </div>
  );
}

export function DiagnosticBanner() {
  return (
    <div className="border border-[#ff00ff]/50 bg-[#ff00ff]/5 p-6 md:p-8 grid md:grid-cols-3 gap-6 items-center" data-testid="diagnostic-fee-banner">
      <div className="md:col-span-2">
        <p className="font-tech text-xs uppercase tracking-[0.25em] text-[#ff00ff] mb-2">Hardware & PC jobs only</p>
        <h3 className="font-heading text-xl sm:text-2xl font-bold text-white mb-2">$75 diagnostic — always credited toward your total</h3>
        <p className="text-sm text-slate-300">
          For hands-on PC and hardware work we start with a $75 diagnostic. It isn't an extra charge — it comes off your final price.
          Remote troubleshooting, business services and web projects have no diagnostic fee.
        </p>
      </div>
      <div className="font-tech text-sm border border-[#00f0ff]/30 bg-[#0a0a1f] p-4 space-y-1.5" data-testid="diagnostic-fee-calc-example">
        <div className="flex justify-between text-slate-300"><span>Virus removal</span><span>$120</span></div>
        <div className="flex justify-between text-[#ff00ff]"><span>Diagnostic now</span><span>− $75</span></div>
        <div className="flex justify-between text-white border-t border-[#00f0ff]/30 pt-1.5"><span>Due when fixed</span><span>$45</span></div>
      </div>
    </div>
  );
}

const STEPS = [
  { icon: MessageSquare, title: 'Chat with our AI', text: 'Describe the problem. Many fixes happen right in the chat — free.' },
  { icon: UserCheck, title: 'Get a technician', text: 'Need more? Add your name and email and a real tech picks it up.' },
  { icon: CreditCard, title: 'Pay only when needed', text: 'Clear prices up front. Web builds start with a free consult.' },
  { icon: CheckCircle2, title: 'Fixed & confirmed', text: 'Your ticket closes only when you say it is resolved.' },
];

export function HowItWorks() {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 border border-[#00f0ff]/30" data-testid="how-it-works-timeline">
      {STEPS.map((s, i) => (
        <div key={s.title} className="p-6 border-b sm:border-r border-[#00f0ff]/20 last:border-r-0" data-testid={`timeline-step-${i + 1}`}>
          <p className="font-display text-4xl font-extrabold text-[#00f0ff]/30 mb-4">0{i + 1}</p>
          <s.icon size={22} strokeWidth={1.5} className="text-[#ff00ff] mb-3" />
          <h3 className="font-heading text-lg font-bold text-white mb-1">{s.title}</h3>
          <p className="text-sm text-slate-400 leading-relaxed">{s.text}</p>
        </div>
      ))}
    </div>
  );
}

const PILLARS = [
  { icon: ShieldCheck, label: 'Security-first', text: 'Every build and fix is hardened by default.' },
  { icon: Gauge, label: 'Value-based pricing', text: 'You pay for results, not stopwatch hours.' },
  { icon: Lock, label: 'No pressure', text: 'Honest advice from a friendly tech pro — never a sales pitch.' },
];

export function MissionStrip() {
  return (
    <div className="grid lg:grid-cols-5 gap-10 items-start" data-testid="mission-philosophy-strip">
      <div className="lg:col-span-2" data-testid="architect-arthur-watts-bio">
        <p className="font-tech text-xs uppercase tracking-[0.3em] text-[#ff00ff] mb-3">Who you're working with</p>
        <h2 className="font-heading text-3xl font-extrabold uppercase text-white mb-3">Led by Arthur Watts</h2>
        <p className="text-slate-300 leading-relaxed">
          Principal Technical Solutions Architect (Maestro AI University). Next Wave Tech Help pairs fast AI-assisted workflows
          with disciplined engineering, so you get modern, resilient results instead of off-the-shelf templates.
        </p>
      </div>
      <div className="lg:col-span-3 grid sm:grid-cols-3 gap-4">
        {PILLARS.map((p) => (
          <div key={p.label} className="nw-card p-5">
            <p.icon size={22} strokeWidth={1.5} className="text-[#00f0ff] mb-3" />
            <h3 className="font-heading font-bold text-white mb-1">{p.label}</h3>
            <p className="text-sm text-slate-400">{p.text}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export function LandingFooter({ onChat }) {
  return (
    <footer className="border-t border-[#00f0ff]/30 mt-24" data-testid="footer-container">
      <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col md:flex-row gap-6 md:items-center justify-between">
        <div>
          <p className="font-heading font-extrabold uppercase tracking-tight text-white">Next Wave Tech Help</p>
          <p className="font-tech text-xs text-[#39ff88] uppercase tracking-widest mt-1 flex items-center gap-2" data-testid="footer-system-uptime">
            <span className="w-2 h-2 bg-[#39ff88]" /> All systems operational
          </p>
        </div>
        <button onClick={onChat} className="nw-btn self-start px-5 py-3 border border-[#00f0ff] text-[#00f0ff] hover:bg-[#00f0ff] hover:text-[#0a0a1f] font-tech uppercase tracking-wider text-xs" data-testid="footer-chat-btn">
          Chat with AI now
        </button>
        <p className="font-tech text-xs text-slate-500" data-testid="footer-copyright-text">© {new Date().getFullYear()} Next Wave Tech Help. All rights reserved.</p>
      </div>
    </footer>
  );
}
